import React, {useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {Modal, Table, Text, Tooltip} from '@forecast-it/design-system';
import {FormattedHTMLMessage, FormattedMessage, useIntl} from 'react-intl';
import {UnassociateBillToModal_viewer} from './__generated__/UnassociateBillToModal_viewer.graphql';
import DeleteClientBillTosMutation from '../../../../mutations/invoicing/DeleteClientBillTosMutation';
import Util from '../../../shared/util/util';

type UnassociateBillToModalProps = {
	billToId: string;
	closeModal: () => void;
	viewer: UnassociateBillToModal_viewer;
};

type Client = {
	id: string;
	name: string;
	clientBillTos: {
		edges: [
			{
				node: {
					billTo: {
						id: string;
					};
					billFrom: {
						id: string;
					};
					clientId: string;
					invoiced: boolean;
					id: string;
				};
			}
		];
	};
};

const UnassociateBillToModal = ({billToId, closeModal, viewer}: UnassociateBillToModalProps) => {
	const {formatMessage} = useIntl();
	const [selectedRows, setSelectedRows] = useState<Client[]>([]);

	// Get clients that have this bill to associated
	const clients = useMemo(
		() =>
			(viewer.company?.clients?.edges
				?.map(edge => edge?.node)
				?.filter(client =>
					client?.clientBillTos?.edges?.some(billTo => billTo?.node?.billTo?.id === billToId)
				) as Client[]) || [],
		[viewer]
	);
	const invoicedClient = useMemo(
		() =>
			new Set(
				clients
					.filter(client =>
						client?.clientBillTos?.edges?.some(
							clientBillTo => clientBillTo?.node?.billTo?.id === billToId && clientBillTo?.node?.invoiced
						)
					)
					.map(client => client.id)
			),
		[clients]
	);

	const unAssociateBillTo = () => {
		if (clients.length === 1) {
			// This is in case there is only one client to un-associate.
			// We do no show a list from which the user can select, but just
			// a confirmation message with the name of the client.
			selectedRows.push(clients[0]);
		}

		// Get the client bill to ids to delete
		const clientBillTosToDelete = selectedRows
			.flatMap(client => client.clientBillTos.edges)
			.filter(clientBillTo => clientBillTo.node.billTo.id === billToId)
			.map(clientBillTo => clientBillTo.node.id);

		Util.CommitMutation(
			DeleteClientBillTosMutation,
			{
				clientBillToIds: clientBillTosToDelete,
			},
			closeModal
		);
	};

	const areThereClients = clients.length > 0;

	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>{formatMessage({id: 'settings_finance.bill_to.unassociate_client'})}</Modal.Title>
			<Modal.Content>
				{areThereClients ? (
					clients.length > 1 ? (
						<>
							<Text size={'1'}>
								<b>{formatMessage({id: 'settings_finance.select_client'})}</b>
							</Text>
							<Text>{formatMessage({id: 'settings_finance.unassociate_single_bill_to_entity'})}</Text>
							<Table
								data={clients}
								onSelectionChange={setSelectedRows}
								width={'100%'}
								enableRowSelection={row => !invoicedClient.has(row.original.id)}
							>
								<Table.TextColumn
									header={formatMessage({id: 'common.select_all'})}
									accessorKey={'name'}
									disabled={row => invoicedClient.has(row.id)}
									tooltip={row =>
										invoicedClient.has(row.id) ? (
											<Tooltip.Item
												title={formatMessage({
													id: 'settings_finance.bill_to.unassociate_client.disabled_tooltip',
												})}
												details={
													<FormattedHTMLMessage
														id={'settings_finance.bill_to.unassociate_client.disabled_tooltip.desc'}
													/>
												}
											/>
										) : undefined
									}
								/>
							</Table>
						</>
					) : (
						<Text>
							<FormattedMessage
								id={'settings_finance.bill_to.unassociate_single_client_message'}
								values={{clientName: clients[0]?.name}}
							/>
						</Text>
					)
				) : (
					<Text size={'1'}>
						{formatMessage({id: 'settings_finance.all_clients_un_associated_from_bill_to_message'})}
					</Text>
				)}
			</Modal.Content>
			{areThereClients ? (
				<Modal.Footer>
					<Modal.PrimaryFooterButton
						onPress={unAssociateBillTo}
						disabled={clients.length > 1 && !selectedRows.length}
					>
						{formatMessage({id: 'common.un_associate'})}
					</Modal.PrimaryFooterButton>

					<Modal.SecondaryFooterButton onPress={closeModal}>
						{formatMessage({id: 'common.close'})}
					</Modal.SecondaryFooterButton>
				</Modal.Footer>
			) : (
				<Modal.Footer>
					<Modal.PrimaryFooterButton onPress={closeModal}>
						{formatMessage({id: 'common.close'})}
					</Modal.PrimaryFooterButton>
				</Modal.Footer>
			)}
		</Modal>
	);
};

export const UnassociateBillToModalQuery = graphql`
	query UnassociateBillToModalQuery {
		viewer {
			actualPersonId
			component(name: "UnsassociateBillToModal")
			...UnassociateBillToModal_viewer
		}
	}
`;

export default createFragmentContainer(UnassociateBillToModal, {
	viewer: graphql`
		fragment UnassociateBillToModal_viewer on Viewer {
			id
			company {
				id
				clients(first: 100000) {
					edges {
						node {
							id
							name
							clientBillTos(first: 10000) @connection(key: "Client_clientBillTos", filters: []) {
								edges {
									node {
										id
										billFrom {
											id
										}
										billTo {
											id
										}
										clientId
										invoiced
									}
								}
							}
						}
					}
				}
			}
		}
	`,
});
